import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Popconfirm,
  Space,
  Card,
  Tag,
  Tabs,
  Descriptions,
  Statistic,
  Row,
  Col,
} from 'antd';
import { PlusOutlined, EditOutlined, DollarOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import api from '../services/api';
import dayjs from 'dayjs';

const { TextArea } = Input;

const CajaChica = () => {
  const [cajasChica, setCajasChica] = useState([]);
  const [gastos, setGastos] = useState([]);
  const [personal, setPersonal] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [gastosModalVisible, setGastosModalVisible] = useState(false);
  const [editingCaja, setEditingCaja] = useState(null);
  const [selectedPersonal, setSelectedPersonal] = useState(null);
  const [form] = Form.useForm();
  const [gastosForm] = Form.useForm();

  useEffect(() => {
    fetchCajasChica();
    fetchGastos();
    fetchPersonal();
  }, []);

  const fetchCajasChica = async () => {
    setLoading(true);
    try {
      const response = await api.get('/caja-chica');
      setCajasChica(response.data);
    } catch (error) {
      message.error('Error al cargar cajas chica');
    } finally {
      setLoading(false);
    }
  };

  const fetchGastos = async () => {
    try {
      const response = await api.get('/gastos-caja-chica');
      setGastos(response.data);
    } catch (error) {
      message.error('Error al cargar gastos');
    }
  };

  const fetchPersonal = async () => {
    try {
      const response = await api.get('/personal');
      setPersonal(response.data.filter(p => p.estado === 'activo'));
    } catch (error) {
      console.error('Error al cargar personal');
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingCaja) {
        await api.put(`/caja-chica/${editingCaja.id}`, values);
        message.success('Caja chica actualizada exitosamente');
      } else {
        await api.post('/caja-chica', values);
        message.success('Caja chica asignada exitosamente');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingCaja(null);
      fetchCajasChica();
    } catch (error) {
      message.error('Error al guardar caja chica');
    }
  };

  const handleEdit = (record) => {
    setEditingCaja(record);
    form.setFieldsValue({
      personal_id: record.personal_id?.toString(),
      monto_asignado: record.monto_asignado,
      observaciones: record.observaciones,
    });
    setModalVisible(true);
  };

  const handleVerGastos = (record) => {
    setSelectedPersonal(record);
    const gastosPersonal = gastos.filter(g => g.personal_id === record.personal_id);
    setGastosModalVisible(true);
  };

  const handleAprobarGasto = async (gastoId) => {
    try {
      await api.put(`/gastos-caja-chica/${gastoId}`, { estado: 'aprobado' });
      message.success('Gasto aprobado exitosamente');
      fetchGastos();
      fetchCajasChica();
    } catch (error) {
      message.error('Error al aprobar gasto');
    }
  };

  const handleRechazarGasto = async (gastoId) => {
    try {
      await api.put(`/gastos-caja-chica/${gastoId}`, { estado: 'rechazado' });
      message.success('Gasto rechazado exitosamente');
      fetchGastos();
      fetchCajasChica();
    } catch (error) {
      message.error('Error al rechazar gasto');
    }
  };

  const getTipoGastoColor = (tipo) => {
    const colores = {
      operativo: 'blue',
      combustible: 'orange',
      peaje: 'green',
      otro: 'default',
    };
    return colores[tipo] || 'default';
  };

  const getEstadoColor = (estado) => {
    const colores = {
      pendiente: 'orange',
      aprobado: 'green',
      rechazado: 'red',
    };
    return colores[estado] || 'default';
  };

  const cajasColumns = [
    {
      title: 'Personal',
      key: 'personal',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.personal_nombre}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>
            {record.personal_documento} - {record.personal_cargo}
          </div>
        </div>
      ),
    },
    {
      title: 'Monto Asignado',
      dataIndex: 'monto_asignado',
      key: 'monto_asignado',
      render: (monto) => monto ? `S/ ${parseFloat(monto).toFixed(2)}` : '-',
    },
    {
      title: 'Monto Disponible',
      dataIndex: 'monto_disponible',
      key: 'monto_disponible',
      render: (monto) => (
        <Tag color={parseFloat(monto || 0) > 0 ? 'green' : 'red'}>
          S/ {parseFloat(monto || 0).toFixed(2)}
        </Tag>
      ),
    },
    {
      title: 'Monto Utilizado',
      key: 'monto_utilizado',
      render: (_, record) => {
        const utilizado = parseFloat(record.monto_asignado || 0) - parseFloat(record.monto_disponible || 0);
        return `S/ ${utilizado.toFixed(2)}`;
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          <Button
            onClick={() => handleVerGastos(record)}
          >
            Ver Gastos
          </Button>
        </Space>
      ),
    },
  ];

  const gastosColumns = [
    {
      title: 'Personal',
      key: 'personal',
      render: (_, record) => record.personal_nombre || '-',
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha_gasto',
      key: 'fecha_gasto',
      render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo_gasto',
      key: 'tipo_gasto',
      render: (tipo) => (
        <Tag color={getTipoGastoColor(tipo)}>
          {tipo ? tipo.toUpperCase() : '-'}
        </Tag>
      ),
    },
    {
      title: 'Monto',
      dataIndex: 'monto',
      key: 'monto',
      render: (monto) => monto ? `S/ ${parseFloat(monto).toFixed(2)}` : '-',
    },
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      render: (text) => text || '-',
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado, record) => (
        <Space>
          <Tag color={getEstadoColor(estado)}>
            {estado ? estado.toUpperCase() : 'PENDIENTE'}
          </Tag>
          {estado === 'pendiente' && (
            <>
              <Button
                type="link"
                icon={<CheckOutlined />}
                onClick={() => handleAprobarGasto(record.id)}
              >
                Aprobar
              </Button>
              <Button
                type="link"
                danger
                icon={<CloseOutlined />}
                onClick={() => handleRechazarGasto(record.id)}
              >
                Rechazar
              </Button>
            </>
          )}
        </Space>
      ),
    },
    {
      title: 'Comprobante',
      key: 'comprobante',
      render: (_, record) => (
        record.comprobante ? (
          <Button
            type="link"
            onClick={() => window.open(record.comprobante, '_blank')}
          >
            Ver
          </Button>
        ) : '-'
      ),
    },
  ];

  const gastosPersonal = selectedPersonal 
    ? gastos.filter(g => g.personal_id === selectedPersonal.personal_id)
    : [];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab="Asignaciones de Caja Chica" key="1">
            <div style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingCaja(null);
                  form.resetFields();
                  setModalVisible(true);
                }}
              >
                Asignar Caja Chica
              </Button>
            </div>
            <Table
              columns={cajasColumns}
              dataSource={cajasChica}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Todos los Gastos" key="2">
            <Table
              columns={gastosColumns}
              dataSource={gastos}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </Tabs.TabPane>
        </Tabs>
      </Card>

      <Modal
        title={editingCaja ? 'Editar Caja Chica' : 'Asignar Caja Chica'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingCaja(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="personal_id"
            label="Personal"
            rules={[{ required: true, message: 'Selecciona el personal' }]}
          >
            <Select placeholder="Selecciona el personal" disabled={!!editingCaja}>
              {personal.map((p) => (
                <Select.Option key={p.id} value={p.id}>
                  {p.nombre_completo} - {p.cargo}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="monto_asignado"
            label="Monto a Asignar"
            rules={[{ required: true, message: 'Ingresa el monto' }]}
          >
            <InputNumber
              min={0}
              precision={2}
              style={{ width: '100%' }}
              placeholder="0.00"
              prefix="S/"
            />
          </Form.Item>

          <Form.Item name="observaciones" label="Observaciones">
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingCaja ? 'Actualizar' : 'Asignar'}
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                  setEditingCaja(null);
                }}
              >
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Gastos de ${selectedPersonal?.personal_nombre || ''}`}
        open={gastosModalVisible}
        onCancel={() => {
          setGastosModalVisible(false);
          setSelectedPersonal(null);
        }}
        footer={null}
        width={900}
      >
        {selectedPersonal && (
          <Descriptions bordered column={2} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Monto Asignado">
              S/ {parseFloat(selectedPersonal.monto_asignado || 0).toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label="Monto Disponible">
              <Tag color={parseFloat(selectedPersonal.monto_disponible || 0) > 0 ? 'green' : 'red'}>
                S/ {parseFloat(selectedPersonal.monto_disponible || 0).toFixed(2)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Monto Utilizado">
              S/ {(parseFloat(selectedPersonal.monto_asignado || 0) - parseFloat(selectedPersonal.monto_disponible || 0)).toFixed(2)}
            </Descriptions.Item>
          </Descriptions>
        )}
        <Table
          columns={gastosColumns}
          dataSource={gastosPersonal}
          rowKey="id"
          pagination={{ pageSize: 5 }}
        />
      </Modal>
    </div>
  );
};

export default CajaChica;

